/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React, { useCallback, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  TicketSalesByOrderDateDashboardSkeleton,
  commonStyles,
} from "./TicketSalesByOrderDateDashboardSkeleton";

import type {
  GetTicketSalesByOrderDateResponse,
  TicketSalesByOrderDate,
} from "./useGetTicketSalesByOrderDate";
import type { TooltipProps } from "recharts";

type ChartRow = {
  label: string;
} & Record<string, number | string>;

type Enumerate<
  N extends number,
  Acc extends number[] = [],
> = Acc["length"] extends N
  ? Acc[number]
  : Enumerate<N, [...Acc, Acc["length"]]>;

// TypeScriptは剰余計算の結果がCHART_COLORSの有効な添字になることまでは推論できないため、参照可能な添字を型で明示する。
type ChartColorIndex = Enumerate<(typeof CHART_COLORS)["length"]>;

const CHART_COLORS = ["#00c2ae", "#5aa9c9", "#9083c9", "#63c3a6"] as const;

export const TicketSalesByOrderDateDashboard: React.FC<{
  data: GetTicketSalesByOrderDateResponse | undefined;
  isLoading: boolean;
}> = ({ data, isLoading }) => {
  const ticketNames = useMemo(() => {
    if (!data) return [];
    return createTicketNames(data.ticketSalesByOrderDate);
  }, [data]);

  const chartData = useMemo(() => {
    if (!data) return undefined;
    return createChartData(data.ticketSalesByOrderDate, ticketNames);
  }, [data, ticketNames]);

  const tooltipContent = useCallback(
    ({ active, payload, label }: TooltipProps<number, string>) => {
      if (!active || !payload || !payload.length) return null;

      return (
        <div
          style={{
            pointerEvents: "none",
            minWidth: 238,
            background: tooltipStyle.background,
            border: "1px solid #2e3d4c",
            borderRadius: 10,
            boxShadow: "0 10px 28px rgba(0, 0, 0, .38)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "9px 14px",
              fontSize: 12.5,
              color: "#b9c3cd",
              letterSpacing: ".4px",
              borderBottom: "1px solid #2e3d4c",
            }}
          >
            {label}
          </div>
          <div style={{ padding: "5px 14px 8px" }}>
            {payload.map((p) => {
              const name = String(p.name ?? p.dataKey ?? "");
              const value = p.value ?? (p.payload as ChartRow)[name] ?? 0;
              const index = ticketNames.indexOf(name);
              const color = getChartColor(index === -1 ? 0 : index);

              return (
                <div
                  key={name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "5px 0",
                  }}
                >
                  <span
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: "50%",
                      background: color,
                      flex: "none",
                    }}
                  />
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: "#dbe6ee",
                    }}
                  >
                    {name}
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#ffffff",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );
    },
    [ticketNames]
  );

  if (isLoading || !data) {
    return <TicketSalesByOrderDateDashboardSkeleton />;
  }

  return (
    <section css={commonStyles.section}>
      <div css={commonStyles.header}>
        <h2 css={commonStyles.title}>日別販売数（券種別）</h2>
        <p css={commonStyles.subtitle}>支払日ごとの販売数</p>
      </div>

      <ul css={styles.legend}>
        {ticketNames.map((name, index) => (
          <li css={styles.legendItem} key={name}>
            <span css={styles.legendMarker(getChartColor(index))} />
            <span>{name}</span>
          </li>
        ))}
      </ul>

      <div css={commonStyles.chartArea}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            barCategoryGap={6}
            barGap={2}
            data={chartData}
            margin={chartMargin}
          >
            <CartesianGrid stroke="#2e3d4c" vertical={false} />
            <XAxis
              axisLine={{ stroke: "#8b96a2" }}
              dataKey="label"
              label={{
                fill: "#8b96a2",
                fontSize: 12,
                position: "bottom",
                offset: 6,
                value: "支払日",
              }}
              minTickGap={18}
              tick={{ fill: "#8b96a2", fontSize: 12 }}
              tickLine={false}
              tickMargin={8}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              label={{
                angle: -90,
                fill: "#8b96a2",
                fontSize: 12,
                position: "left",
                offset: 6,
                value: "販売数",
              }}
              tick={{ fill: "#8b96a2", fontSize: 12 }}
              tickCount={5}
              tickLine={false}
              width={42}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              cursor={{ fill: "rgba(31, 41, 55, 0.04)" }}
              content={tooltipContent}
            />
            {ticketNames.map((name, index) => (
              <Bar
                dataKey={name}
                fill={getChartColor(index)}
                fillOpacity={0.95}
                key={name}
                maxBarSize={28}
                radius={0}
                stackId="sales"
                stroke="#1b2734"
                strokeWidth={1}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

const createTicketNames = (
  ticketSalesByOrderDate: TicketSalesByOrderDate[]
): string[] =>
  Array.from(
    new Set(ticketSalesByOrderDate.map(({ ticketName }) => ticketName))
  );

/**
 * APIの「注文日 x チケット名」の行データを、Rechartsが系列ごとに描画できる日付単位の横持ちデータへ変換する。
 *
 * 変更前: [{ orderDate, ticketName: "A", quantity: 10 }, { orderDate, ticketName: "B", quantity: 3 }]
 * 変更後: [{ label: "06/08", A: 10, B: 3 }]
 */
const createChartData = (
  ticketSalesByOrderDate: TicketSalesByOrderDate[],
  ticketNames: string[]
): ChartRow[] => {
  const rowsByOrderDate = new Map<number, ChartRow>();

  ticketSalesByOrderDate.forEach(({ orderDate, quantity, ticketName }) => {
    const orderDateMilliseconds = Number(orderDate);
    const row =
      rowsByOrderDate.get(orderDateMilliseconds) ??
      createEmptyChartRow(orderDateMilliseconds, ticketNames);

    row[ticketName] = quantity;
    rowsByOrderDate.set(orderDateMilliseconds, row);
  });

  return Array.from(rowsByOrderDate.entries())
    .sort(([a], [b]) => a - b)
    .map(([, row]) => row);
};

const createEmptyChartRow = (
  orderDateMilliseconds: number,
  ticketNames: string[]
): ChartRow => {
  const row: ChartRow = {
    label: formatOrderDate(orderDateMilliseconds),
  };

  ticketNames.forEach((ticketName) => {
    row[ticketName] = 0;
  });

  return row;
};

const getChartColor = (index: number): string => {
  const chartColorIndex = (index % CHART_COLORS.length) as ChartColorIndex;

  return CHART_COLORS[chartColorIndex];
};

const formatOrderDate = (orderDateMilliseconds: number): string =>
  // date-fns/formatは実行環境のローカルタイムゾーンに依存するため、JST日付として返るorderDateの表示もJST固定にする。
  new Intl.DateTimeFormat("ja-JP", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Asia/Tokyo",
  }).format(new Date(orderDateMilliseconds));

const chartMargin = {
  bottom: 24,
  left: 16,
  right: 32,
  top: 16,
};

const tooltipStyle = {
  border: "1px solid #2e3d4c",
  borderRadius: 8,
  boxShadow: "0 10px 24px rgba(31, 41, 55, 0.14)",
  padding: "8px 10px",
  background: "#22313f",
};

const styles = {
  legend: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 0;
    margin: 0 0 10px;
    list-style: none;
  `,
  legendItem: css`
    display: flex;
    align-items: center;
    gap: 6px;
    color: #b9c3cd;
    font-size: 12.5px;
  `,
  legendMarker: (color: string) => css`
    width: 9px;
    height: 9px;
    background: ${color};
    border-radius: 2px;
  `,
};
